/**
 * Table tile body (S4) — a read-only mini-grid built on TanStack Table (repo
 * rule for tabular data: reuse its column sizing/sort model, NOT the
 * editable ResultsGrid/TableExplorerGrid — see CLAUDE.md "Two Results Grids").
 * Local client-side sort + column resize + sticky header; copy row/cell/table
 * reuse the G12 clipboard utils shared with the Query Editor/Table Explorer
 * grids. `rowLimit` trims client-side on top of the executor's own row cap
 * (`rowsCapped`) — either truncation (plus a hard DOM-size safety valve, since
 * this is a small tile, not a full grid) surfaces the same banner so users
 * always know they're seeing a subset.
 *
 * UX-review fixes:
 *  - #1 numeric columns right-align + `tabular-nums` (see `tableTileColumns`).
 *  - #2/#3 the per-row copy button USED TO live in a dedicated leading
 *    `<th>/<td>` gutter column — a permanently blank ~24px column before the
 *    first real column (misaligned with the tile title's ~12px inset) that
 *    ALSO ate a share of the table's stretch width whenever `minWidth:100%`
 *    kicked in (a real column competing with data columns for extra space).
 *    It's now an absolutely-positioned hover overlay inside the first cell —
 *    no extra column, so `table.getTotalSize()` IS the real content width
 *    (no manual "+N" fudge) and 100% of any stretch now goes to real data.
 *    `scrollbarGutter:'stable'` is also gone: it unconditionally reserved
 *    ~15-17px for a vertical scrollbar even on short tiles that never show
 *    one, which is what tipped an already-tight-fitting narrow table (e.g. a
 *    single `id` column) into a phantom horizontal scrollbar.
 *  - #4 a right-edge fade (`hasMoreContentRight`) — native/overlay scrollbars
 *    (macOS "when scrolling" style especially) can be invisible at rest,
 *    silently hiding truncated trailing columns with no cue.
 *  - #5 "Copy table" is now hover-revealed (tile hover/focus-within, like the
 *    kebab in TileFrame), staying visible via `focus-visible` for keyboard use.
 */
import { useMemo, useRef, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TableTile } from '../../types/dashboardTypes';
import { useTileResult } from '../../hooks/useTileResult';
import { QueryTileStates } from './QueryTileStates';
import { buildTableTileColumns, type TableTileRow } from './tableTileColumns';
import { tileEmptyText } from './tileEmptyText';
import { useTileScrollFade } from './useTileScrollFade';
import { useTileTableCopy } from './useTileTableCopy';
import { useTileTableContainerWidth } from './useTileTableContainerWidth';
import { computeFitColumnWidths } from './tileTableFitWidth';

/** Hard safety valve on rendered DOM rows — a dashboard tile is small real
 * estate; nothing needs 10,000 raw `<tr>`s to feel "not capped". Sorting still
 * runs over the FULL delivered set (cheap; only the render is capped). */
const MAX_RENDERED_ROWS = 500;

export function TableTileView({ dashboardId, tile }: { dashboardId: string; tile: TableTile }) {
  const state = useTileResult(dashboardId, tile);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { copyTable, copyRow, copyCell } = useTileTableCopy();
  // `| null` in the generic selects the mutable-ref overload — the callback-ref
  // merge below assigns `scrollRef.current` itself.
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const selected = state.result?.selected;
  const allRows = useMemo(() => selected?.rows ?? [], [selected?.rows]);
  const rowLimit = tile.rowLimit > 0 ? tile.rowLimit : allRows.length;
  const rows = useMemo(() => allRows.slice(0, rowLimit), [allRows, rowLimit]);
  const totalKnownRows = selected?.rowCount ?? allRows.length;

  const { columnDefs, numericColumnIds } = useMemo(
    () => buildTableTileColumns(selected?.columns ?? [], rows, tile),
    [selected?.columns, rows, tile],
  );
  const density = tile.density === 'comfortable' ? 'py-1.5' : 'py-0.5';

  const table = useReactTable<TableTileRow>({
    data: rows,
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
    defaultColumn: { size: 140, minSize: 60 },
  });

  // Responsive fit (S24 F4 follow-up): shrink DEFAULT (never manually
  // resized) columns down to the tile's actual width instead of always
  // rendering at the 140px-per-column default, which overflows a narrow
  // (e.g. "3-of-12") tile and hard-clips the trailing column at the tile's
  // edge — see `tileTableFitWidth.ts` for the full root-cause writeup. A
  // column the user HAS dragged keeps exactly the width they set.
  const { attachRef: attachWidthRef, width: containerWidth } = useTileTableContainerWidth();
  const manuallyResizedIds = new Set(Object.keys(table.getState().columnSizing));
  const columnFitWidths = computeFitColumnWidths(
    table.getAllLeafColumns().map((c) => ({ id: c.id, size: c.getSize(), minSize: c.columnDef.minSize ?? 60 })),
    manuallyResizedIds,
    containerWidth,
  );
  const tableWidth = Array.from(columnFitWidths.values()).reduce((a, b) => a + b, 0);

  const sortedRows = table.getRowModel().rows;
  const renderedRows = sortedRows.slice(0, MAX_RENDERED_ROWS);
  const capped = Boolean(state.result?.rowsCapped) || renderedRows.length < totalKnownRows;

  // Finding #4: native/overlay scrollbars can be invisible at rest, silently
  // hiding truncated trailing columns — re-measured on scroll, container
  // resize, and whenever the table's own content width changes.
  const showRightFade = useTileScrollFade(scrollRef, [tableWidth, renderedRows.length]);

  return (
    <QueryTileStates state={state}>
      {rows.length === 0 ? (
        <div className="flex h-full items-center justify-center text-xs text-muted-foreground" data-testid="table-tile-empty">
          {tileEmptyText(tile, 'Query returned no rows')}
        </div>
      ) : (
        <div className="flex h-full min-h-0 flex-col" data-testid="table-tile-body">
          <div className="flex items-center justify-end gap-2 border-b border-border/60 px-2 py-1">
            <button
              type="button"
              // Hover-revealed like the rest of the tile chrome (finding #5):
              // hidden until the tile is hovered/focus-within (`group/tile`,
              // established by TileFrame), always shown once keyboard-focused.
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/tile:opacity-100"
              title="Copy table"
              onClick={() => selected && copyTable(selected)}
              data-testid="table-tile-copy-table"
            >
              <Copy className="h-3 w-3" aria-hidden /> Copy table
            </button>
          </div>
          <div className="relative min-h-0 flex-1">
            <div
              ref={(el) => {
                scrollRef.current = el;
                attachWidthRef(el);
              }}
              className="h-full overflow-auto"
              data-testid="table-tile-scroll"
            >
              {/* table-fixed: with auto layout a cell never shrinks below its
                  unbreakable min-content (long column names), so the pinned
                  width is ignored and truncate/ellipsis never engages; fixed
                  layout honors the pinned widths exactly. minWidth stretches
                  the table to the tile when columns underfill it, so a narrow
                  tile doesn't show a phantom horizontal scrollbar (S24 F4/F45).
                  `tableWidth` sums `columnFitWidths` — the tile-responsive
                  shrink of the default per-column size (see
                  `tileTableFitWidth.ts`) — no gutter column exists anymore to
                  account for (see file header, #2/#3). */}
              <table className="table-fixed border-collapse text-xs" style={{ width: tableWidth, minWidth: '100%' }}>
                <thead className="sticky top-0 z-10 bg-card">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => {
                        const isNumeric = numericColumnIds.has(header.column.id);
                        return (
                          <th
                            key={header.id}
                            style={{ width: columnFitWidths.get(header.column.id) ?? header.getSize() }}
                            className={cn(
                              'relative select-none border-b border-border py-1 font-medium text-muted-foreground',
                              // First column gets a touch more left room than
                              // the tile title's px-3 (12px) inset — the
                              // row-copy button overlays this cell on hover
                              // (was a permanent 32px+ dead gutter column).
                              index === 0 ? 'pl-5 pr-2' : 'px-2',
                              isNumeric ? 'text-right' : 'text-left',
                            )}
                          >
                            <button
                              type="button"
                              className={cn(
                                'flex w-full items-center gap-1 truncate',
                                isNumeric && 'justify-end',
                                header.column.getCanSort() && 'cursor-pointer hover:text-foreground',
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                              data-testid="table-tile-header"
                              data-column={header.id}
                            >
                              {/* min-w-0: without it this span is a flex item that
                                  can't shrink, so the button clips the text with NO
                                  ellipsis and the title tooltip site never truncates
                                  visibly (S24 F45). */}
                              <span className="min-w-0 truncate" title={String(header.column.columnDef.header)}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {header.column.getIsSorted() === 'asc' && <span aria-hidden>↑</span>}
                              {header.column.getIsSorted() === 'desc' && <span aria-hidden>↓</span>}
                            </button>
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none hover:bg-border"
                              data-testid="table-tile-resize-handle"
                            />
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {renderedRows.map((row) => (
                    <tr key={row.id} className="group/row border-b border-border/60 hover:bg-muted/40" data-testid="table-tile-row">
                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={cell.id}
                          style={{ width: columnFitWidths.get(cell.column.id) ?? cell.column.getSize() }}
                          className={cn('cursor-pointer', density, index === 0 ? 'relative pl-5 pr-2' : 'px-2')}
                          title="Double-click to copy cell"
                          onDoubleClick={() => copyCell(cell.getValue())}
                          data-testid="table-tile-data-cell"
                        >
                          {index === 0 && (
                            <button
                              type="button"
                              className="absolute left-0.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100"
                              title="Copy row"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selected) copyRow(selected.columns, row.original);
                              }}
                              onDoubleClick={(e) => e.stopPropagation()}
                              data-testid="table-tile-copy-row"
                            >
                              <Copy className="h-3 w-3" aria-hidden />
                            </button>
                          )}
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {showRightFade && (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 z-20 w-6 bg-gradient-to-l from-card to-transparent"
                aria-hidden
                data-testid="table-tile-scroll-fade"
              />
            )}
          </div>
          {capped && (
            <div
              className="border-t border-amber-500/30 bg-amber-500/10 px-2 py-1 text-center text-[11px] text-amber-700 dark:text-amber-400"
              data-testid="table-tile-capped-banner"
            >
              Showing first {renderedRows.length.toLocaleString()} of {totalKnownRows.toLocaleString()} rows — results were capped.
            </div>
          )}
        </div>
      )}
    </QueryTileStates>
  );
}
